/* AIReviewSphere.js
 *
 * This component renders a visual indicator for AI review outcome based on score + confidence.
 * - outcome === "ai_holds"  -> textured “magnetic moons” sphere (canvas)
 * - outcome === "needs_review" -> animated ring with a gradient tail (canvas)
 *
 * NOTE: The needs-review path now renders a SINGLE ring (no multi-panel strip),
 * so it stays within a typical DataGrid cell width when centered.
 */

import React, { useEffect, useRef, useMemo } from "react";
import { clamp, getOutcome, getReviewType } from "../utils/AiReviewHelpers";
import { deriveTheme } from "../components/aiReview/deriveTheme";

// ──────────────────────────────────────────────────────────────────────
// Helpers (USED)
// ──────────────────────────────────────────────────────────────────────

function rgba(rgb, a) {
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
}

function mixRgb(a, b, t) {
  const tt = clamp(t, 0, 1);
  return {
    r: Math.round(a.r + (b.r - a.r) * tt),
    g: Math.round(a.g + (b.g - a.g) * tt),
    b: Math.round(a.b + (b.b - a.b) * tt),
  };
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return null;
  const h = hex.replace("#", "").trim();

  if (h.length === 3) {
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }

  if (h.length === 6) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }

  return null;
}

function getConfidenceColourPath(confidence) {
  if (confidence === "high") return "successColour";
  if (confidence === "medium") return "warningColour";
  return "errorColour";
}

function getConfidenceBaseRgb(theme, confidence) {
  const colourPath = getConfidenceColourPath(confidence);
  return hexToRgb(theme[colourPath]);
}

function scoreToUnit(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return 0;
  return clamp(s, 0, 5) / 5;
}

// ──────────────────────────────────────────────────────────────────────
// Decision Logic (EXPORTED; KEEP)
// ──────────────────────────────────────────────────────────────────────

export { getOutcome, getReviewType };

// ──────────────────────────────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────────────────────────────

export default function AIReviewSphere({
  score,
  confidence,
  theme = {},
  size = 46,
  styleVariant = "corporate", // kept for API stability (currently unused here)
}) {
  const outcome = getOutcome(score, confidence);
  const T = useMemo(() => deriveTheme(theme), [theme]);
  const baseRgb = getConfidenceBaseRgb(T, confidence);

  if (!baseRgb) return null;

  if (outcome === "ai_holds") {
    return (
      <TexturedMagneticMoonsSphere
        score={score}
        confidence={confidence}
        baseRgb={baseRgb}
        size={size}
      />
    );
  }

  return (
    <SingleRing
      score={score}
      confidence={confidence}
      baseRgb={baseRgb}
      size={size}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────
// Needs-review (SINGLE ring only; no multi-panel strip)
// ──────────────────────────────────────────────────────────────────────

function SingleRing({ score, confidence, baseRgb, size = 46 }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: size,
        width: size,
        flexShrink: 0,
      }}
    >
      <GradientTailArcRingFullCircleFixedDelay
        score={score}
        confidence={confidence}
        baseRgb={baseRgb}
        size={size}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Needs-review ring: Full circle + FIXED delay after each revolution (USED)
// ──────────────────────────────────────────────────────────────────────

function GradientTailArcRingFullCircleFixedDelay({
  score,
  confidence,
  baseRgb,
  size = 46,
  glyph = "human",
  showGuide = true,
}) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const lastRef = useRef(null);

  const blueRgb = useMemo(() => ({ r: 59, g: 130, b: 246 }), []);

  const pauseUntilRef = useRef(0);
  const jitterRef = useRef(0.94 + Math.random() * 0.12);
  const phaseRef = useRef(Math.random());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 2;

    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);

    const scoreClamped = clamp(Number(score) || 0, 0, 5);
    const unit = scoreToUnit(scoreClamped);

    const speedPerSec = (0.45 + 0.4 * unit) * jitterRef.current;
    const delaySeconds = 1.05 - 0.8 * unit;

    const startAngle = -Math.PI / 2;

    const thickness = Math.max(4.8, size * 0.145);
    const outerPad = Math.max(0.6, size * 0.015);
    const ringR = size / 2 - outerPad - thickness / 2;

    const draw = (now) => {
      if (lastRef.current == null) lastRef.current = now;
      const dt = Math.min(0.05, (now - lastRef.current) / 1000);
      lastRef.current = now;
      const t = now / 1000;
      const nowSec = now / 1000;

      let wrappedThisFrame = false;
      let isPaused = nowSec < pauseUntilRef.current;

      if (!isPaused) {
        const next = phaseRef.current + dt * speedPerSec;

        if (next >= 1) {
          wrappedThisFrame = true;
          phaseRef.current = next % 1;
          pauseUntilRef.current = nowSec + Math.max(0, delaySeconds);
        } else {
          phaseRef.current = next;
        }
      }

      if (wrappedThisFrame) isPaused = true;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size, size);

      const cx = size / 2;
      const cy = size / 2;

      if (showGuide) {
        ctx.save();
        ctx.strokeStyle = "rgba(0,0,0,0.18)";
        ctx.lineWidth = 0.9;
        ctx.beginPath();
        ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      if (unit > 0.0001) {
        const endAngle = startAngle + Math.PI * 2 * unit;

        ctx.save();
        ctx.lineCap = "round";
        ctx.strokeStyle = rgba(baseRgb, 0.32);
        ctx.lineWidth = thickness;
        ctx.beginPath();
        ctx.arc(cx, cy, ringR, startAngle, endAngle, false);
        ctx.stroke();
        ctx.restore();

        if (!isPaused) {
          const arcLen = endAngle - startAngle;
          const head = startAngle + phaseRef.current * (Math.PI * 2);

          const tailFrac = 0.36 + (1 - unit) * 0.14;
          const tail = arcLen * tailFrac;

          const steps = 22;
          for (let i = 0; i < steps; i++) {
            const u = i / (steps - 1);
            const ang = head - u * tail;

            if (ang < startAngle || ang > endAngle) continue;

            const hot = Math.exp(-u * 3.7);
            const a = 0.05 + 0.55 * hot;
            const span = 0.105 - 0.04 * u;

            ctx.save();
            ctx.strokeStyle = rgba(baseRgb, a);
            ctx.lineWidth = thickness * (0.78 + 0.18 * hot);
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.arc(cx, cy, ringR, ang - span, ang + span, false);
            ctx.stroke();
            ctx.restore();
          }
        }
      }

      if (glyph === "clipboard") {
        drawClipboardChecksGlyph(ctx, cx, cy, size, blueRgb, t, phaseRef.current);
      } else {
        drawHumanMagnifierGlyph(ctx, cx, cy, size, t, phaseRef.current);
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      animRef.current = null;
      lastRef.current = null;
      pauseUntilRef.current = 0;
    };
  }, [size, score, baseRgb, blueRgb, glyph, showGuide]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size, display: "block" }}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────
// High-confidence sphere (USED)
// ──────────────────────────────────────────────────────────────────────

function TexturedMagneticMoonsSphere({ score, confidence, baseRgb, size = 46 }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const t0Ref = useRef(null);

  const seedRef = useRef(Math.random() * 9999);
  const phaseRef = useRef(Math.random());
  const jitterRef = useRef(0.88 + Math.random() * 0.24);

  const texRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 2;

    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);

    if (!texRef.current) {
      const c = document.createElement("canvas");
      texRef.current = { c, ctx: c.getContext("2d") };
    }

    const texCanvas = texRef.current.c;
    const texCtx = texRef.current.ctx;

    texCanvas.width = size;
    texCanvas.height = size;

    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };

    const scoreClamped = clamp(Number(score) || 0, 0, 5);
    const unit = scoreToUnit(scoreClamped);

    const fract = (x) => x - Math.floor(x);
    const lerp = (a, b, t) => a + (b - a) * t;

    const hash2 = (x, y) => {
      const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
      return fract(s);
    };

    const valueNoise2 = (x, y) => {
      const xi = Math.floor(x);
      const yi = Math.floor(y);
      const xf = x - xi;
      const yf = y - yi;

      const a = hash2(xi, yi);
      const b = hash2(xi + 1, yi);
      const c = hash2(xi, yi + 1);
      const d = hash2(xi + 1, yi + 1);

      const u = xf * xf * (3 - 2 * xf);
      const v = yf * yf * (3 - 2 * yf);

      return lerp(lerp(a, b, u), lerp(c, d, u), v);
    };

    const fbm2 = (x, y, octaves = 4) => {
      let f = 0;
      let amp = 0.55;
      let freq = 1;
      let norm = 0;

      for (let i = 0; i < octaves; i++) {
        f += valueNoise2(x * freq, y * freq) * amp;
        norm += amp;
        amp *= 0.55;
        freq *= 2.0;
      }

      return norm > 0 ? f / norm : 0;
    };

    const hash = (n) => fract(Math.sin(n * 127.1) * 43758.5453123);

    const drawSoftShadow = (cx, cy, R) => {
      ctx.save();
      ctx.globalAlpha = 0.14;
      ctx.filter = "blur(2.1px)";
      ctx.fillStyle = "#000";
      ctx.translate(cx, cy + R + 7);
      ctx.scale(1, 0.24);
      ctx.beginPath();
      ctx.arc(0, 0, R * 0.78, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    const drawSphereBody = (cx, cy, R) => {
      const edge = mixRgb(baseRgb, black, 0.45);
      const g = ctx.createRadialGradient(
        cx - R * 0.35,
        cy - R * 0.45,
        0,
        cx + R * 0.2,
        cy + R * 0.2,
        R * 1.25
      );
      g.addColorStop(0, rgba(mixRgb(baseRgb, white, 0.86), 0.95));
      g.addColorStop(0.3, rgba(mixRgb(baseRgb, white, 0.55), 0.9));
      g.addColorStop(0.65, rgba(mixRgb(baseRgb, white, 0.22), 0.86));
      g.addColorStop(1, rgba(edge, 0.9));

      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawHighlightAndRim = (cx, cy, R) => {
      const hl = ctx.createRadialGradient(
        cx - R * 0.28,
        cy - R * 0.34,
        0,
        cx - R * 0.1,
        cy - R * 0.16,
        R * 0.6
      );
      hl.addColorStop(0, "rgba(255,255,255,0.78)");
      hl.addColorStop(0.18, "rgba(255,255,255,0.32)");
      hl.addColorStop(0.55, "rgba(255,255,255,0.07)");
      hl.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = hl;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = rgba(mixRgb(baseRgb, white, 0.65), 0.22);
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.stroke();
    };

    const buildMarbleTextureStrong = (theta) => {
      const TW = texCanvas.width;
      const TH = texCanvas.height;

      const img = texCtx.createImageData(TW, TH);
      const data = img.data;

      const texAmp = 0.48;
      const alpha = 0.92;
      const blurPx = 0.06;

      const ct = Math.cos(theta);
      const st = Math.sin(theta);

      const tilt = 0.18;
      const cxr = Math.cos(tilt);
      const sxr = Math.sin(tilt);

      const seedA = 17.3 + seedRef.current * 0.0001;
      const seedB = 91.2 + phaseRef.current * 3.1;

      const ocx = size / 2;
      const ocy = size / 2 - 1;
      const oR = size * 0.34;

      for (let y = 0; y < TH; y++) {
        for (let x = 0; x < TW; x++) {
          const dx = (x + 0.5 - ocx) / oR;
          const dy = (y + 0.5 - ocy) / oR;
          const rr = dx * dx + dy * dy;

          const idx = (y * TW + x) * 4;

          if (rr > 1) {
            data[idx + 3] = 0;
            continue;
          }

          const nz = Math.sqrt(Math.max(0, 1 - rr));
          const limb = Math.pow(nz, 0.92);

          const nx = dx;
          const ny = dy;

          const nx1 = nx * ct + nz * st;
          const nz1 = -nx * st + nz * ct;

          const ny2 = ny * cxr - nz1 * sxr;

          const big = fbm2(nx1 * 0.5 + seedA, ny2 * 0.5 + seedB, 3);
          const mid = fbm2(nx1 * 1.3 + seedB, ny2 * 1.3 + seedA, 4);
          const warp = fbm2(nx1 * 0.85 + 3.7, ny2 * 0.85 + 8.1, 3);
          const veins =
            0.5 +
            0.5 * Math.sin((nx1 * 2.0 + ny2 * 1.6 + warp * 2.1) * 2.9);

          const n = big * 0.64 + mid * 0.16 + veins * 0.2;

          let centered = (n - 0.5) * 2;
          centered = Math.sign(centered) * Math.pow(Math.abs(centered), 0.72);

          const g = 128 + centered * (128 * texAmp) * limb;

          data[idx + 0] = g;
          data[idx + 1] = g;
          data[idx + 2] = g;
          data[idx + 3] = 255;
        }
      }

      texCtx.setTransform(1, 0, 0, 1, 0, 0);
      texCtx.clearRect(0, 0, TW, TH);
      texCtx.putImageData(img, 0, 0);

      return { alpha, blurPx };
    };

    const drawTextureOnSphere = (cx, cy, R, theta) => {
      const { alpha, blurPx } = buildMarbleTextureStrong(theta);

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R - 0.7, 0, Math.PI * 2);
      ctx.clip();

      ctx.globalCompositeOperation = "soft-light";
      ctx.globalAlpha = alpha;
      ctx.filter = `blur(${blurPx}px)`;
      ctx.drawImage(texCanvas, cx - size / 2, cy - (size / 2 - 1), size, size);

      ctx.restore();
      ctx.filter = "none";
      ctx.globalCompositeOperation = "source-over";
    };

    const drawMagneticMoonsOverlay = (cx, cy, t, R) => {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R - 0.6, 0, Math.PI * 2);
      ctx.clip();

      const localSeed = seedRef.current;
      const spin =
        t * (0.7 + unit * 1.0) * jitterRef.current +
        phaseRef.current * Math.PI * 2 +
        localSeed * 0.002;

      const loops = [
        { tilt: 0.55, a: 0.36, w: 1.55 },
        { tilt: 1.05, a: 0.24, w: 1.25 },
      ];

      for (let i = 0; i < loops.length; i++) {
        const { tilt, a, w } = loops[i];
        const yScale = 0.3 + 0.55 * Math.abs(Math.cos(tilt));
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(spin + i * 0.9);
        ctx.scale(1, yScale);
        ctx.strokeStyle = rgba(mixRgb(baseRgb, white, 0.9), a);
        ctx.lineWidth = w;
        ctx.beginPath();
        ctx.arc(0, 0, R * 0.92, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      const pathR = R * 0.78;
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      ctx.strokeStyle = rgba(mixRgb(baseRgb, white, 0.75), 0.14);
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.ellipse(cx, cy, pathR, pathR * 0.62, spin * 0.35, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      const moonCount = clamp(Math.round(scoreClamped), 0, 5);

      for (let i = 0; i < moonCount; i++) {
        const p = moonCount <= 1 ? 0 : i / moonCount;

        const rrBase = R * (0.58 + 0.3 * hash(localSeed * 9.3 + i * 13.1));
        const spd =
          (1.55 + 0.28 * i) *
          (0.9 + 0.35 * hash(localSeed * 3.7 + i * 7.9));
        const ang = spin * spd + p * Math.PI * 2;

        const ySquash = 0.62 + 0.2 * Math.sin(spin + i);
        const x = cx + Math.cos(ang) * rrBase;
        const y = cy + Math.sin(ang) * rrBase * ySquash;

        const tint = clamp(0.7 + i * 0.08, 0, 0.95);
        const moonRgb = mixRgb(baseRgb, white, tint);

        const glowR = R * 0.26;
        const g = ctx.createRadialGradient(x, y, 0, x, y, glowR);
        g.addColorStop(0, rgba(mixRgb(moonRgb, white, 0.25), 0.72));
        g.addColorStop(0.6, rgba(moonRgb, 0.24));
        g.addColorStop(1, rgba(baseRgb, 0));

        ctx.save();
        ctx.globalCompositeOperation = "screen";
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, glowR, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        ctx.fillStyle = rgba(mixRgb(moonRgb, white, 0.35), 0.9);
        ctx.beginPath();
        ctx.arc(x, y, 2.0, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.beginPath();
        ctx.arc(x - 0.6, y - 0.6, 0.7, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      ctx.globalCompositeOperation = "source-over";
    };

    const draw = (now) => {
      if (t0Ref.current == null) t0Ref.current = now;
      const t = (now - t0Ref.current) / 1000;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size, size);

      const cx = size / 2;
      const cy = size / 2 - 1;
      const R = size * 0.34;

      const scoreInt = clamp(Math.round(scoreClamped), 0, 5);
      const GEAR_RAD_PER_SEC = [0.9, 1.3, 1.9, 2.8, 4.1, 6.0];
      const radPerSec = GEAR_RAD_PER_SEC[scoreInt] * (0.92 + jitterRef.current * 0.1);
      const theta = t * radPerSec + phaseRef.current * Math.PI * 2;

      drawSoftShadow(cx, cy, R);
      drawSphereBody(cx, cy, R);
      drawTextureOnSphere(cx, cy, R, theta);
      drawMagneticMoonsOverlay(cx, cy, t, R);
      drawHighlightAndRim(cx, cy, R);

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      animRef.current = null;
      t0Ref.current = null;
    };
  }, [size, score, baseRgb]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size, display: "block" }}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────
// Glyph helpers (USED by ring)
// ──────────────────────────────────────────────────────────────────────

function drawRoundedRectPath(ctx, x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

function drawHumanMagnifierGlyph(ctx, cx, cy, size, t, phase) {
  const pulse = 0.5 + 0.5 * Math.sin(t * 2.2 + phase * Math.PI * 2);

  const headRgb = { r: 60, g: 60, b: 70 };
  const magnifierRgb = { r: 59, g: 130, b: 246 };
  const overlapRgb = { r: 245, g: 250, b: 255 };

  const headR = size * 0.085;
  const headY = cy - size * 0.06;

  const mx = cx + size * 0.11;
  const my = cy + size * 0.02;
  const mr = size * (0.085 + 0.01 * pulse);

  const ringWidth = Math.max(1.4, size * 0.032);

  ctx.save();
  ctx.fillStyle = rgba(headRgb, 0.95);
  ctx.beginPath();
  ctx.arc(cx - size * 0.02, headY, headR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.fillStyle = rgba(headRgb, 0.88);
  ctx.beginPath();
  ctx.ellipse(
    cx - size * 0.02,
    cy + size * 0.04,
    size * 0.15,
    size * 0.11,
    0,
    0,
    Math.PI * 2
  );
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.globalCompositeOperation = "screen";
  ctx.strokeStyle = rgba(magnifierRgb, 0.75 + 0.2 * pulse);
  ctx.lineWidth = ringWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  ctx.beginPath();
  ctx.arc(mx, my, mr, 0, Math.PI * 2);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(mx + mr * 0.65, my + mr * 0.65);
  ctx.lineTo(mx + mr * 1.25, my + mr * 1.25);
  ctx.stroke();
  ctx.restore();

  const cacheKey = `${size}`;
  if (!drawHumanMagnifierGlyph._cache) drawHumanMagnifierGlyph._cache = {};
  if (!drawHumanMagnifierGlyph._cache[cacheKey]) {
    const mask = document.createElement("canvas");
    const ring = document.createElement("canvas");
    mask.width = size;
    mask.height = size;
    ring.width = size;
    ring.height = size;

    drawHumanMagnifierGlyph._cache[cacheKey] = {
      mask,
      mctx: mask.getContext("2d"),
      ring,
      rctx: ring.getContext("2d"),
    };
  }

  const { mask, mctx, ring, rctx } = drawHumanMagnifierGlyph._cache[cacheKey];

  mctx.setTransform(1, 0, 0, 1, 0, 0);
  mctx.clearRect(0, 0, size, size);
  mctx.fillStyle = "rgba(255,255,255,1)";

  mctx.beginPath();
  mctx.arc(cx - size * 0.02, headY, headR, 0, Math.PI * 2);
  mctx.fill();

  mctx.beginPath();
  mctx.ellipse(
    cx - size * 0.02,
    cy + size * 0.04,
    size * 0.15,
    size * 0.11,
    0,
    0,
    Math.PI * 2
  );
  mctx.fill();

  rctx.setTransform(1, 0, 0, 1, 0, 0);
  rctx.clearRect(0, 0, size, size);
  rctx.strokeStyle = rgba(overlapRgb, 1);
  rctx.lineWidth = ringWidth;
  rctx.lineCap = "round";
  rctx.lineJoin = "round";
  rctx.beginPath();
  rctx.arc(mx, my, mr, 0, Math.PI * 2);
  rctx.stroke();

  rctx.globalCompositeOperation = "destination-in";
  rctx.drawImage(mask, 0, 0);
  rctx.globalCompositeOperation = "source-over";

  ctx.save();
  ctx.globalCompositeOperation = "screen";
  ctx.globalAlpha = 0.95;
  ctx.drawImage(ring, 0, 0, size, size);
  ctx.restore();
}

function drawClipboardChecksGlyph(ctx, cx, cy, size, blueRgb, t, phase) {
  const pulse = 0.5 + 0.5 * Math.sin(t * 2.0 + phase * Math.PI * 2);

  const w = size * 0.3;
  const h = size * 0.34;
  const x = cx - w * 0.45;
  const y = cy - h * 0.3;

  ctx.save();
  ctx.globalAlpha = 0.75;
  ctx.fillStyle = rgba(blueRgb, 0.38);
  drawRoundedRectPath(ctx, x, y, w, h, 3);
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.globalAlpha = 0.75;
  ctx.fillStyle = rgba(blueRgb, 0.55);
  drawRoundedRectPath(ctx, cx - w * 0.16, y - h * 0.12, w * 0.32, h * 0.16, 2);
  ctx.fill();
  ctx.restore();

  const checks = 2;
  const cycle = (t * 0.9 + phase) % 1;

  for (let i = 0; i < checks; i++) {
    const rowY = y + h * (0.36 + i * 0.28);
    const leftX = x + w * 0.18;

    const local = (cycle + i * 0.35) % 1;
    const k = clamp((local - 0.15) / 0.55, 0, 1);

    ctx.save();
    ctx.globalAlpha = 0.18 + 0.1 * pulse;
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.34, rowY);
    ctx.lineTo(x + w * 0.86, rowY);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.strokeStyle = rgba(blueRgb, 0.7 + 0.2 * pulse);
    ctx.lineWidth = 1.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const p1x = leftX;
    const p1y = rowY;
    const p2x = leftX + w * 0.08;
    const p2y = rowY + h * 0.06;
    const p3x = leftX + w * 0.2;
    const p3y = rowY - h * 0.06;

    const k1 = clamp(k * 1.35, 0, 1);
    const k2 = clamp((k - 0.45) * 1.8, 0, 1);

    ctx.beginPath();
    ctx.moveTo(p1x, p1y);
    ctx.lineTo(p1x + (p2x - p1x) * k1, p1y + (p2y - p1y) * k1);
    if (k2 > 0) {
      ctx.lineTo(p2x + (p3x - p2x) * k2, p2y + (p3y - p2y) * k2);
    }
    ctx.stroke();
    ctx.restore();
  }
}
